import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Tab, Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import ManualQuote from './ManualQuote';

function Part() {
  const { user } = useAuth();
  const { part_number } = useParams();
  const navigate = useNavigate();
  const [part, setPart] = useState(null);
  const [partFiles, setPartFiles] = useState([]);
  const [productionLogs, setProductionLogs] = useState([]);
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({ section: null, enabled: false });
  const [formData, setFormData] = useState({});
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileType, setFileType] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    const fetchPartDetails = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data: partData, error: partError } = await supabase
          .from('parts')
          .select(`
            part_number,
            part_number_master,
            description,
            revision,
            material,
            shop_id,
            machine_id,
            last_run_date,
            average_run_time,
            setup_notes,
            stock_usage,
            minimum_stock_level,
            kanban_stock,
            created_at,
            updated_at,
            status,
            unit_price,
            supplier_id,
            production_notes,
            batch_size,
            inventory_quantity,
            shops(id, name),
            machines(id, name),
            suppliers(id, name)
          `)
          .eq('part_number', part_number)
          .single();
        if (partError) throw partError;
        setPart(partData);
        setFormData(partData);

        const { data: files, error: filesError } = await supabase
          .from('part_files')
          .select('id, part_number, file_name, file_path, file_type, uploaded_at')
          .eq('part_number', part_number);
        if (filesError) throw filesError;
        setPartFiles(files || []);

        const { data: logs, error: logsError } = await supabase
          .from('production_logs')
          .select('log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id, shops(id, name), machines(id, name)')
          .eq('part_number', part_number);
        if (logsError) throw logsError;
        setProductionLogs(logs || []);

        const { data: quotesData, error: quotesError } = await supabase
          .from('quotes')
          .select('*')
          .eq('part_number', part_number);
        if (quotesError) throw quotesError;
        setQuotes(quotesData || []);

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name');
        if (machinesError) throw machinesError;
        setMachines(machinesData || []);

        const { data: suppliersData, error: suppliersError } = await supabase
          .from('suppliers')
          .select('id, name');
        if (suppliersError) throw suppliersError;
        setSuppliers(suppliersData || []);
      } catch (err) {
        console.error('Error fetching part details:', err);
        console.log('Failed to fetch part details.');
      } finally {
        setLoading(false);
      }
    };

    fetchPartDetails();
  }, [user, part_number]);

  useEffect(() => {
    document.title = `${part ? part.part_number : 'Loading...'}`;
  }, [part]);

  const handleEditToggle = (section) => {
    if (editMode.section === section && editMode.enabled) {
      setEditMode({ section: null, enabled: false });
    } else {
      setEditMode({ section, enabled: true });
      setFormData({ ...part });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async (section) => {
    try {
      const updatedData = {};
      if (section === 'general') {
        updatedData.part_number_master = formData.part_number_master || null;
        updatedData.description = formData.description || null;
        updatedData.revision = formData.revision || null;
        updatedData.material = formData.material || null;
        updatedData.part_number = `${formData.part_number_master || ''}-${formData.revision || '0'}`;
      } else if (section === 'production') {
        updatedData.last_run_date = formData.last_run_date || null;
        updatedData.average_run_time = formData.average_run_time ? parseFloat(formData.average_run_time) : null;
        updatedData.setup_notes = formData.setup_notes || null;
        updatedData.batch_size = formData.batch_size ? parseInt(formData.batch_size) : null;
      } else if (section === 'inventory') {
        updatedData.unit_price = formData.unit_price ? parseFloat(formData.unit_price) : null;
        
        if (formData.inventory_adjustment) {
          const adjustmentValue = parseInt(formData.inventory_adjustment);
          if (!isNaN(adjustmentValue)) {
            const currentQuantity = part.inventory_quantity || 0;
            updatedData.inventory_quantity = currentQuantity + adjustmentValue;
            if (updatedData.inventory_quantity < 0) {
              updatedData.inventory_quantity = 0;
              console.log('Inventory cannot go below zero. Setting to zero.');
            }
          }
        }
      } else if (section === 'meta') {
        updatedData.shop_id = formData.shop_id ? parseInt(formData.shop_id) : null;
        updatedData.machine_id = formData.machine_id ? parseInt(formData.machine_id) : null;
        updatedData.supplier_id = formData.supplier_id ? parseInt(formData.supplier_id) : null;
        updatedData.production_notes = formData.production_notes || null;
        updatedData.status = formData.status || 'Active';
      }
  
      const { error } = await supabase
        .from('parts')
        .update(updatedData)
        .eq('part_number', part_number);
      if (error) throw error;
  
      const updatedPart = {
        ...part,
        ...updatedData,
        shops: part.shops,
        machines: part.machines,
        suppliers: part.suppliers,
      };
      setPart(updatedPart);
  
      if (section === 'general') {
        const newPartNumber = `${formData.part_number_master || ''}-${formData.revision || '0'}`;
        if (newPartNumber !== part_number) {
          navigate(`/part/${newPartNumber}`);
        }
      }
  
      window.location.reload();
  
      setEditMode({ section: null, enabled: false });
      console.log('Part details updated successfully!');
    } catch (err) {
      console.error('Error updating part:', err);
      console.log('Failed to update part details.');
    }
  };

  const handleFileUpload = async () => {
    if (!fileToUpload || !fileType) {
      console.log('Please select a file and file type.');
      return;
    }

    try {
      const filePath = `part-files/${part_number}/${fileToUpload.name}`;
      const { error: uploadError } = await supabase.storage
        .from('part-files')
        .upload(filePath, fileToUpload);
      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from('part_files')
        .insert({
          part_number,
          file_name: fileToUpload.name,
          file_path: filePath,
          file_type: fileType,
          uploaded_by: user.id,
        })
        .select();
      if (insertError) throw insertError;

      setPartFiles([...partFiles, data[0]]);
      setFileToUpload(null);
      setFileType('');
      setIsUploadModalOpen(false);
      console.log('File uploaded successfully!');
    } catch (err) {
      console.error('Error uploading file:', err);
      console.log('Failed to upload file.');
    }
  };

  const handleFileDownload = async (file_path) => {
    try {
      const { data, error } = await supabase.storage
        .from('part-files')
        .download(file_path);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file_path.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log('File downloaded successfully!');
    } catch (err) {
      console.error('Error downloading file:', err);
      console.log('Failed to download file.');
    }
  };

  const handleFilePreview = async (file_path) => {
    try {
      const { data, error } = await supabase.storage
        .from('part-files')
        .createSignedUrl(file_path, 60);
      if (error) throw error;

      setPreviewFile(data.signedUrl);
      setIsPreviewModalOpen(true);
    } catch (err) {
      console.error('Error generating preview URL:', err);
      console.log('Failed to preview file.');
    }
  };

  const handleFileDelete = async (file_id, file_path) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this file?');
    if (!userConfirmed) return;
    try {
      const { error: deleteError } = await supabase.storage
        .from('part-files')
        .remove([file_path]);
      if (deleteError) throw deleteError;

      const { error: dbError } = await supabase
        .from('part_files')
        .delete()
        .eq('id', file_id);
      if (dbError) throw dbError;

      setPartFiles(partFiles.filter(file => file.id !== file_id));
      console.log('File deleted successfully!');
    } catch (err) {
      console.error('Error deleting file:', err);
      console.log('Failed to delete file.');
    }
  };

  const handleQuoteSave = async (quoteData) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .insert({
          part_number,
          part_number_display: quoteData.partNumber,
          revision: quoteData.revision,
          description: quoteData.description,
          quantity: parseInt(quoteData.quantity),
          price_each: parseFloat(quoteData.priceEach),
          quote_number: quoteData.quoteNumber,
          date: quoteData.date,
          good_until: quoteData.goodUntil,
          pricing_ranges: quoteData.pricingRanges,
          customer_data: quoteData.customerData,
          created_by: user.id,
        });
      if (error) throw error;

      const { data: newQuotes } = await supabase
        .from('quotes')
        .select('*')
        .eq('part_number', part_number);
      setQuotes(newQuotes || []);
      console.log('Quote saved successfully!');
    } catch (err) {
      console.error('Error saving quote:', err);
      console.log('Failed to save quote.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Obsolete':
        return 'bg-red-100 text-red-800';
      case 'In Development':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeletePart = async () => {
    const userConfirmed = window.confirm('Are you sure you want to delete this part? This action cannot be undone.');
    if (!userConfirmed) return;

    try {
      const { data: associatedJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id, po_number')
        .eq('part_number', part_number);
      if (jobsError) throw new Error(`Failed to check associated jobs: ${jobsError.message}`);

      if (associatedJobs && associatedJobs.length > 0) {
        const jobList = associatedJobs.map(job => `Job ID: ${job.id}, PO Number: ${job.po_number}`).join('\n');
        window.log(`Cannot delete part. It is associated with the following jobs:\n${jobList}\nPlease remove the part from these jobs first.`);
        return;
      }

      if (partFiles.length > 0) {
        const filePaths = partFiles.map(file => file.file_path);
        const { error: storageError } = await supabase.storage
          .from('part-files')
          .remove(filePaths);
        if (storageError) throw new Error(`Failed to delete part files from storage: ${storageError.message}`);

        const { error: filesError } = await supabase
          .from('part_files')
          .delete()
          .eq('part_number', part_number);
        if (filesError) throw new Error(`Failed to delete part files from database: ${filesError.message}`);
      }

      const { error: logsError } = await supabase
        .from('production_logs')
        .delete()
        .eq('part_number', part_number);
      if (logsError) throw new Error(`Failed to delete production logs: ${logsError.message}`);

      const { error: quotesError } = await supabase
        .from('quotes')
        .delete()
        .eq('part_number', part_number);
      if (quotesError) throw new Error(`Failed to delete quotes: ${quotesError.message}`);

      const { error: partError } = await supabase
        .from('parts')
        .delete()
        .eq('part_number', part_number);
      if (partError) throw new Error(`Failed to delete part: ${partError.message}`);

      navigate('/parts');
    } catch (err) {
      console.error('Error deleting part:', err);
    }
  };

  const getStockStatusColor = (kanbanStock, minimumStockLevel) => {
    if (kanbanStock === null || minimumStockLevel === null) return 'text-gray-600';
    return kanbanStock < minimumStockLevel ? 'text-red-600' : 'text-green-600';
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;
  if (!part) return <div className="text-center text-red-500 py-10">Part not found</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto bg-gray-50">
        <div className="flex justify-between items-center mb-6 bg-gray-50">
          <h1 className="text-3xl font-bold text-gray-900">
            Part Details: {part.part_number}
          </h1>
          <button
            onClick={handleDeletePart}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 shadow-sm"
          >
            Delete Part
          </button>
        </div>

        <Tab.Group>
          <Tab.List className="flex space-x-4 border-b border-gray-200 mb-6">
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              Overview
            </Tab>
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              Production Logs
            </Tab>
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              Files
            </Tab>
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              Quote
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              <div className="bg-white rounded-lg p-6 mb-6 border border-2 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">General Information</h2>
                  <button
                    onClick={() => handleEditToggle('general')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                      editMode.section === 'general' && editMode.enabled
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {editMode.section === 'general' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {editMode.section === 'general' && editMode.enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Part Number Master</label>
                      <input
                        type="text"
                        value={formData.part_number_master || ''}
                        onChange={(e) => handleInputChange('part_number_master', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter part number master"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Revision</label>
                      <input
                        type="text"
                        value={formData.revision || ''}
                        onChange={(e) => handleInputChange('revision', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter revision"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={formData.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Material</label>
                      <input
                        type="text"
                        value={formData.material || ''}
                        onChange={(e) => handleInputChange('material', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter material"
                      />
                    </div>
                    <div className="flex justify-end col-span-2 mt-2">
                      <button
                        onClick={() => handleSave('general')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Part Number Master</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.part_number_master || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Revision</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.revision || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.description || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Material</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.material || '-'}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 mb-6 border border-2 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Production Specifications</h2>
                  <button
                    onClick={() => handleEditToggle('production')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                      editMode.section === 'production' && editMode.enabled
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {editMode.section === 'production' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {editMode.section === 'production' && editMode.enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Run Date</label>
                      <input
                        type="date"
                        value={formData.last_run_date ? new Date(formData.last_run_date).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleInputChange('last_run_date', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Average Run Time (Days)</label>
                      <input
                        type="number"
                        value={formData.average_run_time || ''}
                        onChange={(e) => handleInputChange('average_run_time', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        min="0"
                        placeholder="Enter average run time"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Setup Notes</label>
                      <textarea
                        value={formData.setup_notes || ''}
                        onChange={(e) => handleInputChange('setup_notes', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter setup notes"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Batch Size</label>
                      <input
                        type="number"
                        value={formData.batch_size || ''}
                        onChange={(e) => handleInputChange('batch_size', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        min="0"
                        placeholder="Enter batch size"
                      />
                    </div>
                    <div className="flex justify-end col-span-2 mt-2">
                      <button
                        onClick={() => handleSave('production')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Run Date</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.last_run_date ? new Date(part.last_run_date).toLocaleDateString() : '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Average Run Time (Days)</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.average_run_time || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Setup Notes</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50" title={part.setup_notes}>{part.setup_notes || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Batch Size</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.batch_size || '-'}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 mb-6 border border-2 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Inventory & Cost</h2>
                  <button
                    onClick={() => handleEditToggle('inventory')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                      editMode.section === 'inventory' && editMode.enabled
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {editMode.section === 'inventory' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {editMode.section === 'inventory' && editMode.enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Inventory</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                        {part.inventory_quantity || '0'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                      <input
                        type="number"
                        value={formData.unit_price || ''}
                        onChange={(e) => handleInputChange('unit_price', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        min="0"
                        step="0.01"
                        placeholder="Enter unit price"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Inventory Adjustment</label>
                      <div className="flex items-center mt-1">
                        <input
                          type="number"
                          value={formData.inventory_adjustment || ''}
                          onChange={(e) => handleInputChange('inventory_adjustment', e.target.value)}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          placeholder="Enter adjustment amount (positive to add, negative to subtract)"
                        />
                        <span className="ml-2 text-sm text-gray-500">
                          (Enter positive value to add, negative to subtract)
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end col-span-2 mt-2">
                      <button
                        onClick={() => handleSave('inventory')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Inventory</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                        {part.inventory_quantity || '0'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">
                        ${part.unit_price || '0.00'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 mb-6 border border-2 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Supplier & Meta Data</h2>
                  <button
                    onClick={() => handleEditToggle('meta')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 shadow-sm ${
                      editMode.section === 'meta' && editMode.enabled
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {editMode.section === 'meta' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {editMode.section === 'meta' && editMode.enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shop</label>
                      <select
                        value={formData.shop_id || ''}
                        onChange={(e) => handleInputChange('shop_id', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      >
                        <option value="">Select Shop</option>
                        {shops.map(shop => (
                          <option key={shop.id} value={shop.id}>{shop.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Machine</label>
                      <select
                        value={formData.machine_id || ''}
                        onChange={(e) => handleInputChange('machine_id', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      >
                        <option value="">Select Machine</option>
                        {machines.map(machine => (
                          <option key={machine.id} value={machine.id}>{machine.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Supplier</label>
                      <select
                        value={formData.supplier_id || ''}
                        onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={formData.status || ''}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      >
                        <option value="Active">Active</option>
                        <option value="Obsolete">Obsolete</option>
                        <option value="In Development">In Development</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Production Notes</label>
                      <textarea
                        value={formData.production_notes || ''}
                        onChange={(e) => handleInputChange('production_notes', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        placeholder="Enter production notes"
                        rows="3"
                      />
                    </div>
                    <div className="flex justify-end col-span-2 mt-2">
                      <button
                        onClick={() => handleSave('meta')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shop</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.shops?.name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Machine</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.machines?.name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Supplier</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{part.suppliers?.name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{new Date(part.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Updated</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50">{new Date(part.updated_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Production Notes</label>
                      <p className="mt-1 p-2 border border-gray-200 rounded-lg bg-gray-50" title={part.production_notes}>{part.production_notes || '-'}</p>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Panel>

            <Tab.Panel>
              <div className="bg-white rounded-lg p-6 border border-2 shadow-sm">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Production Logs</h2>
                {productionLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity Produced</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Production Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {productionLogs.map((log) => (
                          <tr key={log.log_id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.job_id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.quantity_produced}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.production_date).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.shops?.name || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.machines?.name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No production logs recorded for this part.</p>
                )}
              </div>
            </Tab.Panel>

            <Tab.Panel>
              <div className="bg-white rounded-lg p-6 border border-2 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Files</h2>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                  >
                    Upload File
                  </button>
                </div>
                {partFiles.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded At</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {partFiles.map((file) => (
                          <tr key={file.id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{file.file_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{file.file_type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(file.uploaded_at).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleFilePreview(file.file_path)}
                                className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 text-sm mr-2"
                              >
                                Preview
                              </button>
                              <button
                                onClick={() => handleFileDownload(file.file_path)}
                                className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 text-sm mr-2"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleFileDelete(file.id, file.file_path)}
                                className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-sm"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No files uploaded for this part.</p>
                )}
              </div>
            </Tab.Panel>

            <Tab.Panel>
              <div className="bg-white rounded-lg p-6 border border-2 shadow-sm">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Generate Quote</h2>
                <ManualQuote
                  part={{
                    partNumber: part.part_number_master,
                    revision: part.revision,
                    description: part.description,
                  }}
                  onSave={handleQuoteSave}
                />
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      <Transition appear show={isUploadModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsUploadModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Upload New File
                  </Dialog.Title>
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">File</label>
                        <input
                          type="file"
                          onChange={(e) => setFileToUpload(e.target.files[0])}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">File Type</label>
                        <select
                          value={fileType}
                          onChange={(e) => setFileType(e.target.value)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        >
                          <option value="">Select File Type</option>
                          <option value="Drawing">Drawing</option>
                          <option value="PO">PO</option>
                          <option value="Material Invoice">Material Invoice</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-4">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                      onClick={() => {
                        setFileToUpload(null);
                        setFileType('');
                        setIsUploadModalOpen(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      onClick={handleFileUpload}
                    >
                      Upload
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isPreviewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsPreviewModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="
                    w-[90vw]
                    h-[90vh]
                    max-w-[calc(100vw-2rem)]
                    max-h-[calc(100vh-2rem)]
                    transform
                    rounded-2xl
                    bg-white
                    p-6
                    text-left
                    align-middle
                    shadow-xl
                    transition-all
                  "
                >
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    File Preview
                  </Dialog.Title>

                  <div className="mt-4 h-[calc(100%-6rem)] overflow-auto">
                    {previewFile ? (
                      <iframe
                        src={previewFile}
                        className="w-full h-full border border-gray-200 rounded-lg"
                        title="File Preview"
                      />
                    ) : (
                      <p className="text-gray-500">Unable to preview this file type.</p>
                    )}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                      onClick={() => setIsPreviewModalOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default Part;